import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { GameGroup } from "services/bonus/type"

import { FreeSpinGamesPicker } from "../bonus-config/FreeSpinGamesPicker"

interface GameGroupManagementProps {
  tenant: string
}

interface GroupFormState {
  id?: string
  name: string
  game_ids: string[]
}

const EMPTY_FORM: GroupFormState = { name: "", game_ids: [] }

/**
 * Game group management screen (Req 6) — CREATE / edit / delete reusable named groups of
 * game ids independent of a bonus. Mirrors the BannerManagement / ViolationsDashboard tab
 * layout. Game ids are chosen via the shared FreeSpinGamesPicker, with a paste/comma list
 * fallback for bulk entry.
 */
export const GameGroupManagement: React.FC<GameGroupManagementProps> = ({ tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [groups, setGroups] = useState<GameGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<GroupFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadGroups()
    setForm(EMPTY_FORM)
  }, [tenant])

  const loadGroups = async () => {
    setLoading(true)
    const data = await AdminBonus2Service.listGameGroups(tenant)
    if (data) {
      setGroups(data)
    }
    setLoading(false)
  }

  const startEdit = (group: GameGroup) => {
    setForm({ id: group.id, name: group.name, game_ids: group.game_ids })
  }

  const resetForm = () => setForm(EMPTY_FORM)

  const handleSave = async () => {
    if (!form.name) {
      toast.error(t("bonus2.groups.toastNameRequired") as string)
      return
    }
    setSaving(true)
    const result = await AdminBonus2Service.upsertGameGroup({ id: form.id, name: form.name, game_ids: form.game_ids }, tenant)
    setSaving(false)
    if (result) {
      toast.success((form.id ? t("bonus2.groups.toastUpdated") : t("bonus2.groups.toastCreated")) as string)
      resetForm()
      loadGroups()
    } else {
      toast.error(t("bonus2.groups.toastSaveFailed") as string)
    }
  }

  const handleDelete = async (groupId: string) => {
    if (!window.confirm(t("bonus2.groups.confirmDelete") as string)) return
    const ok = await AdminBonus2Service.deleteGameGroup(groupId, tenant)
    if (ok) {
      toast.success(t("bonus2.groups.toastDeleted") as string)
      if (form.id === groupId) resetForm()
      loadGroups()
    } else {
      toast.error(t("bonus2.groups.toastDeleteFailed") as string)
    }
  }

  // Paste/comma-list fallback for bulk game-id entry — kept in sync with the picker.
  const handlePasteIds = (raw: string) => {
    const ids = raw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    setForm((prev) => ({ ...prev, game_ids: Array.from(new Set(ids)) }))
  }

  const inputClassName = "w-full input input-bordered bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)]"
  const labelClassName = "label-text dark:text-gray-300"

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Game Groups ({groups.length})</h2>
        <button onClick={loadGroups} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors">
          {t("bonus2.groups.refresh")}
        </button>
      </div>

      {/* Create / edit form */}
      <div className="bg-[#1A1A2E] border border-gray-700 rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-semibold text-white">{form.id ? t("bonus2.groups.editTitle") : t("bonus2.groups.createTitle")}</h3>

        <div className="form-control">
          <label className="label">
            <span className={labelClassName}>{t("bonus2.groups.name")}</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            className={inputClassName}
          />
        </div>

        <FreeSpinGamesPicker
          title={t("bonus2.groups.games")}
          selectedGameIds={form.game_ids}
          onGameIdsChange={(gameIds) => setForm((prev) => ({ ...prev, game_ids: gameIds }))}
          tenant={tenant}
        />

        <div className="form-control">
          <label className="label">
            <span className={labelClassName}>…or paste game ids (comma/space separated)</span>
          </label>
          <textarea
            value={form.game_ids.join(", ")}
            onChange={(e) => handlePasteIds(e.target.value)}
            className={`${inputClassName} h-24`}
            placeholder="game-id-1, game-id-2, game-id-3"
          />
        </div>

        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} className="btn btn-primary">
            {saving ? t("bonus2.form.saving") : form.id ? t("bonus2.groups.updateGroup") : t("bonus2.groups.createGroup")}
          </button>
          {form.id && (
            <button onClick={resetForm} className="btn btn-secondary">
              {t("bonus2.groups.cancelEdit")}
            </button>
          )}
        </div>
      </div>

      {/* Existing groups */}
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="text-white">{t("bonus2.groups.loadingGameGroups")}</div>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-[#1A1A2E] border border-gray-700 rounded-lg p-8 text-center">
          <div className="text-gray-400">{t("bonus2.groups.noGameGroupsFound")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.id} className="bg-[#1A1A2E] border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-white font-semibold">{group.name}</div>
                  <div className="text-gray-400 text-sm">{group.game_ids.length} games</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(group)} className="btn btn-sm btn-secondary">
                    {t("bonus2.groups.edit")}
                  </button>
                  <button onClick={() => handleDelete(group.id)} className="btn btn-sm btn-error">
                    {t("bonus2.groups.delete")}
                  </button>
                </div>
              </div>
              {group.game_ids.length > 0 && (
                <div className="text-xs text-gray-300 font-mono max-h-24 overflow-y-auto bg-[#0C0C1A] rounded p-2">
                  {group.game_ids.join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
